import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import Journeys from '@/pages/journeys';
import { LastWorkspaceResolver, LazyPage } from '@/routes/-components';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { substitutePathParams } from '@/services/router/generate-path';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { Navigate } from '@/utils/navigate';
import { PushSettingsLoading } from './components/push-settings-loading';
import { usePushPlatforms } from './hooks';
import WorkspacePushAndroidSettingsPage from './android/index.page';
import WorkspacePushSettingsPage from './index.page';
import WorkspacePushIosSettingsPage from './ios/index.page';
import { MOBILE_APPS_PATH, PUSH_PLATFORM_ROUTES } from './route-paths';

const NO_ACCESS_MESSAGE =
  "You do not have permission to view workspace settings. If you'd like access, let an admin know.";

type PushSettingsRouteProps = {
  children: ReactNode;
};

/**
 * Multi-app workspaces manage push per app, so these URLs belong to the mobile apps
 * settings there. Forwarded before anything reads the credential slots.
 */
function MultiAppForward({ children }: PushSettingsRouteProps) {
  const isMultiApp = useFeatureFlag(AccountFeatureFlag.MultiApp);
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  if (!isMultiApp) {
    return <>{children}</>;
  }

  return (
    <SafeNavigate
      to={substitutePathParams(MOBILE_APPS_PATH, { workspaceId: workspaceId ?? 'last' })}
      options={{ shouldReplaceHistoryEntry: true }}
    />
  );
}

/**
 * The order matters: the rollout flag first, so an account without it keeps the
 * embedded app; then the workspace and its access; then the multi-app forward.
 */
function PushSettingsRouteFrame({ children }: PushSettingsRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.PushSettingsMigration);

  if (!isMigrated) {
    return <Journeys />;
  }

  return (
    <LastWorkspaceResolver>
      <ProtectedRoute
        permission="journeys/workspace_settings/view"
        allowReadOnlyMasqAccess={true}
        redirectTo="/settings"
        errorMessage={NO_ACCESS_MESSAGE}
      >
        <MultiAppForward>
          <LazyPage>{children}</LazyPage>
        </MultiAppForward>
      </ProtectedRoute>
    </LastWorkspaceResolver>
  );
}

/**
 * Opens on the platform already sending, or iOS when neither is. The redirect
 * replaces, so the bare push URL never lands in history and Back leaves the section.
 */
function PushSettingsIndex() {
  const router = useRouter();
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const { platforms, isPending } = usePushPlatforms(workspaceId ?? undefined);

  if (isPending) {
    return <PushSettingsLoading />;
  }

  const target = platforms[0]?.key ?? 'ios';
  const href = router.buildLocation({
    to: PUSH_PLATFORM_ROUTES[target],
    params: { workspaceId: workspaceId ?? ('last' as const) },
  }).href;

  return <Navigate to={href} replace />;
}

/** The gate here also covers the platform tabs, which render into this layout's outlet. */
export function WorkspacePushSettingsLayoutRoute() {
  return (
    <PushSettingsRouteFrame>
      <RumPageRendered>
        <WorkspacePushSettingsPage />
      </RumPageRendered>
    </PushSettingsRouteFrame>
  );
}

export function WorkspacePushSettingsIndexRoute() {
  return <PushSettingsIndex />;
}

export function WorkspacePushIosSettingsRoute() {
  return (
    <RumPageRendered>
      <WorkspacePushIosSettingsPage />
    </RumPageRendered>
  );
}

export function WorkspacePushAndroidSettingsRoute() {
  return (
    <RumPageRendered>
      <WorkspacePushAndroidSettingsPage />
    </RumPageRendered>
  );
}
